import { z } from 'zod'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import { t } from '@/lang'
import { isoFromUk } from '@/lib/date'

/**
 * What the form accepts in a field, as opposed to which fields it has - that is `schema.ts`.
 * These are the rules that answer to something outside the form. Nothing here knows what
 * a consignment is, which is why it lives apart.
 */

/** Ceilings the form fields and the DTO checks in the route handlers share. */
export const MAX_TEXT = 100
export const MAX_EMAIL = 254
export const MAX_DESCRIPTION = 200
export const MAX_PACKAGES = 100
export const MAX_WEIGHT_KG = 999_999
/** Four digits, which is what `sideCm` lets through. */
export const MAX_SIDE_CM = 9_999

/** Every field the guest can see is required, so this is the rule most of them share. */
export const requiredText = (message: string, max = MAX_TEXT) =>
  z.string().trim().min(1, message).max(max, t('validation.too-long', { max }))

/** Company fields are empty until the legal-entity box is ticked, so only the length binds. */
export const optionalText = (max = MAX_TEXT) => z.string().trim().max(max, t('validation.too-long', { max }))

/**
 * GOV.UK asks services not to restrict the characters a name may hold; this restricts none,
 * it only asks that a letter be among them.
 */
const madeOfLetters = (allowed: RegExp) => (value: string) =>
  value === '' || (allowed.test(value) && /\p{Script=Latin}/u.test(value))

const latinMessage = t('validation.latin')
export const latinName = /^[\p{Script=Latin}\p{M} '’.-]+$/u

/**
 * What a registered name and a street need that a person's name does not: numbers, and the
 * punctuation that comes with them - `Sark Traders Ltd.`, `12/14 La Collinette`.
 */
export const latinWithNumbers = /^[\p{Script=Latin}\p{M}\d &'’.,()/-]+$/u
const latinWithNumbersMessage = t('validation.latin-with-numbers')

export const personName = (message: string) => requiredText(message).refine(madeOfLetters(latinName), latinMessage)
export const placeName = optionalText().refine(madeOfLetters(latinName), latinMessage)
export const companyName = optionalText().refine(madeOfLetters(latinWithNumbers), latinWithNumbersMessage)
export const streetAddress = optionalText().refine(madeOfLetters(latinWithNumbers), latinWithNumbersMessage)

/**
 * GOV.UK keeps the word `valid` out of error messages - it repeats that something is wrong
 * without saying what to do. The `like name@example.com` it suggests is left out: the message must
 * fit one line in a third of the form at 768px, or the fields below it jump.
 * https://design-system.service.gov.uk/components/error-message/
 */
export const emailAddress = (message: string) =>
  requiredText(message, MAX_EMAIL).pipe(z.email(t('validation.email-format')))

/** Goods description: letters, numbers and ordinary punctuation */
const plainText = /^[\p{Script=Latin}\p{M}\d '’"().,:;!?&+/-]+$/u

/**
 * The backend asks only that the field be there, but a single letter
 * describes nothing. Two is as high as the floor can go without turning away `TV`.
 */
export const goodsDescription = (message: string, max: number) =>
  z
    .string()
    .trim()
    .min(2, message)
    .max(max, t('validation.too-long', { max }))
    .refine(madeOfLetters(plainText), latinWithNumbersMessage)
    .refine((value) => !/^[+-]/.test(value), t('validation.start-with-letter'))

/** Parse complete input only. An extension cannot be represented in the field's E.164 value. */
const parsePhone = (value: string) => {
  const phone = parsePhoneNumberFromString(value, { defaultCountry: 'GB', extract: false })
  return phone?.ext ? undefined : phone
}

/** Keep what the guest typed in the field; this is the separate value for a future API payload. */
export const normalisePhone = (value: string) => parsePhone(value)?.number

/** A generous cap on typed punctuation, not on the E.164 number's fifteen digits. */
export const PHONE_LENGTH = 64

const contactIncomplete = t('validation.phone-incomplete')

/** One local error at a time. Accept landlines and mobiles alike. */
export const contactNumber = (requiredMessage: string) =>
  z.string().superRefine((value, ctx) => {
    let message: string | undefined
    if (!value.trim()) message = requiredMessage
    else if (value.length > PHONE_LENGTH) message = t('validation.too-long', { max: PHONE_LENGTH })
    else {
      const phone = parsePhone(value)
      const digits = value.replace(/\D/g, '')
      if (!phone?.isPossible()) message = contactIncomplete
      else if (!value.trimStart().startsWith('+') && !digits.startsWith('00') && !digits.startsWith('0')) {
        message = contactIncomplete
      } else if (phone.countryCallingCode === '44' && !phone.isValid()) {
        message = contactIncomplete
      }
    }
    if (message) ctx.addIssue({ code: 'custom', message })
  })

/**
 * The regular expression the UK government publishes for bulk data transfer, run against the
 * postcode without its spaces - a postcode may be written `GY10 1SD` or `GY101SD` and both
 * mean the same place. Covers Guernsey and Sark, GY1 to GY10.
 * https://design-system.service.gov.uk/patterns/addresses/
 */
const ukPostcode =
  /^(GIR0AA|([A-Z][0-9]{1,2}|[A-Z][A-HJ-Y][0-9]{1,2}|[A-Z][0-9][A-Z]|[A-Z][A-HJ-Y][0-9]?[A-Z])[0-9][A-Z]{2})$/

/** Capitals, no spaces: the form of a postcode the backend is given. */
const bare = (value: string) => value.toUpperCase().replaceAll(' ', '')

/** The longest a postcode gets is `DN55 1PT` or `GY10 1SD` - four, a space, three. */
export const POSTCODE_LENGTH = 8

/** One message for every way of getting it wrong, including leaving it empty. */
export const postcodeMessage = t('validation.postcode')

/**
 * Spaces are the only thing taken out before the check.
 */
export const postcode = z
  .string()
  .trim()
  // Length, character set and shape are one question with one answer, so they are one check.
  .refine(
    (value) =>
      value === '' ||
      (value.length <= POSTCODE_LENGTH && /^[A-Za-z0-9 ]+$/.test(value) && ukPostcode.test(bare(value))),
    postcodeMessage
  )
  .transform(bare)

/** What a count or a side takes as it is typed: digits only. */
export const digitCharacters = (value: string) => value.replace(/\D/g, '')

/**
 * What the weight field takes as it is typed: digits and one decimal point with one digit after it; a comma
 * counts as the point. This is the whole format rule, so the schema below has no format message to give.
 */
export const weightCharacters = (value: string) =>
  value
    .replaceAll(',', '.')
    .replace(/[^\d.]/g, '')
    .replace(/(?<=\..*)\./g, '')
    .replace(/(?<=\.\d)\d+/, '')

/**
 * Empty is allowed in both of these; what makes a measurement required is the chosen category.
 */
export const weight = z
  .string()
  .trim()
  .refine(
    (value) => {
      const number = Number(value)
      return !Number.isFinite(number) || number <= MAX_WEIGHT_KG
    },
    t('validation.weight-max', { max: MAX_WEIGHT_KG })
  )

export const sideCm = z
  .string()
  .trim()
  .refine(
    (value) => {
      const number = Number(value)
      return !Number.isFinite(number) || number <= MAX_SIDE_CM
    },
    t('validation.side-max', { max: MAX_SIDE_CM })
  )

export const wholeCount = (message: string, max: number) =>
  z
    .string()
    .trim()
    .refine((value) => /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= max, message)

export const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' })

/** The far end of what the form takes: a year out. */
export const aYearFromToday = () => {
  const date = new Date()
  date.setFullYear(date.getFullYear() + 1)
  return date.toLocaleDateString('en-CA', { timeZone: 'Europe/London' })
}

/** A day in the design's `dd/mm/yyyy`, from today to a year out. One local error at a time. */
export const upcomingDate = (requiredMessage: string) =>
  z.string().superRefine((value, ctx) => {
    const iso = isoFromUk(value)
    let message: string | undefined
    if (!value) message = requiredMessage
    else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) message = t('validation.date-format')
    else if (!iso) message = t('validation.date-nonexistent')
    // ISO dates compare as strings.
    else if (iso < today()) message = t('validation.date-past')
    else if (iso > aYearFromToday()) message = t('validation.date-too-far')
    if (message) ctx.addIssue({ code: 'custom', message })
  })
